import React, { useState, useRef, useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useV2Filters } from '../context/V2FiltersContext';
import type { PeriodValue } from '../context/V2FiltersContext';

export interface AppliedChip {
  id: string;
  category: string;
  label: string;
  /** Hover text for a warning icon shown on the chip */
  warning?: string;
}

interface V2ContextBarProps {
  /** Page-specific toolbar rendered below the context row (filters, chips, search, etc.) */
  children?: React.ReactNode;
}

/**
 * Main Context Bar — matches prototype `.main-context`.
 * White rounded card: CONTEXT | BRAND | MARKET | PERIOD | CONTENT.
 * Page-specific controls are passed as children and rendered in a second row.
 *
 * Pages that need a different section order/content (e.g. Global View) don't
 * use this component — they compose the exported `Bar`/`Section`/`BarDivider`
 * primitives and the `*Section` pieces below directly, in whatever order
 * matches the prototype for that page.
 */
export const V2ContextBar: React.FC<V2ContextBarProps> = ({ children }) => (
  <Wrapper>
    <Bar>
      <ContextToggleSection />
      <BarDivider />
      <BrandChipSection />
      <BarDivider />
      <MarketChipSection />
      <BarDivider />
      <PeriodSelectSection />
      <BarDivider />
      <ContentToggleSection />
    </Bar>

    {/* Page-specific second row (filters, chips, competition, etc.) */}
    {children}
  </Wrapper>
);

export const ContextToggleSection: React.FC<{
  stacked?: boolean;
  label?: string;
  onTradeLabel?: string;
  offTradeLabel?: string;
}> = ({
  stacked,
  label = 'Context',
  onTradeLabel = 'ON',
  offTradeLabel = 'OFF'
}) => {
  const { filters, channelOptions, setChannel } = useV2Filters();
  return (
    <Section $stacked={stacked}>
      <Label>{label}</Label>
      <Toggle>
        {channelOptions.map((opt) => (
          <ToggleBtn
            key={opt.value}
            type="button"
            $active={filters.channel === opt.value}
            $stacked={stacked}
            onClick={() => setChannel(opt.value)}
          >
            {opt.value === 'on_trade' && <OnTradeIcon />}
            {opt.value === 'off_trade' && <OffTradeIcon />}
            {opt.value === 'on_trade'
              ? onTradeLabel
              : opt.value === 'off_trade'
                ? offTradeLabel
                : opt.label}
          </ToggleBtn>
        ))}
      </Toggle>
    </Section>
  );
};

export const ContentToggleSection: React.FC<{
  stacked?: boolean;
  label?: string;
  fullLabel?: string;
}> = ({ stacked, label = 'Content', fullLabel = 'All' }) => {
  const { filters, setContentMode } = useV2Filters();
  return (
    <Section $stacked={stacked}>
      <Label>{label}</Label>
      <Toggle>
        <ToggleBtn
          type="button"
          $active={filters.contentMode === 'premium'}
          $stacked={stacked}
          onClick={() => setContentMode('premium')}
        >
          Premium
        </ToggleBtn>
        <ToggleBtn
          type="button"
          $active={filters.contentMode === 'full'}
          $stacked={stacked}
          onClick={() => setContentMode('full')}
        >
          {fullLabel}
        </ToggleBtn>
      </Toggle>
    </Section>
  );
};

export const BrandChipSection: React.FC<{
  label?: string;
  stacked?: boolean;
  neutral?: boolean;
}> = ({ label = 'Brand', stacked, neutral }) => {
  const { filters, brandOptions, setBrand, isLoading } = useV2Filters();
  return (
    <Section $stacked={stacked}>
      <Label>{label}</Label>
      <ChipDropdown
        options={brandOptions.map((brand) => ({
          value: brand.value,
          label: brand.label
        }))}
        value={filters.brand}
        onChange={setBrand}
        placeholder="All"
        variant="brand"
        neutral={neutral}
        disabled={isLoading}
      />
    </Section>
  );
};

export const MarketChipSection: React.FC<{
  stacked?: boolean;
  neutral?: boolean;
}> = ({ stacked, neutral }) => {
  const { filters, marketOptions, setMarket, isLoading } = useV2Filters();
  return (
    <Section $stacked={stacked}>
      <Label>Market</Label>
      <ChipDropdown
        options={marketOptions.map((market) => ({
          value: market.value,
          label: market.label
        }))}
        value={filters.market}
        onChange={setMarket}
        placeholder="Select market"
        variant="dark"
        neutral={neutral}
        disabled={isLoading}
      />
    </Section>
  );
};

export const PeriodSelectSection: React.FC<{ stacked?: boolean }> = ({
  stacked
}) => {
  const { filters, setPeriod } = useV2Filters();
  const periodOptions: { value: PeriodValue; label: string }[] = [
    { value: '3m', label: '3 months' },
    { value: '6m', label: '6 months' },
    { value: '12m', label: '12 months' }
  ];
  return (
    <Section $stacked={stacked}>
      <Label>Period</Label>
      <PeriodSelect
        value={filters.period}
        onChange={(e) => setPeriod(e.target.value as PeriodValue)}
      >
        {periodOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </PeriodSelect>
    </Section>
  );
};

// ─── ChipDropdown (mc-chip + mc-dropdown) ───────────────────────────────────

export interface ChipDropdownProps {
  options: { value: string; label: string }[];
  value: string | null;
  onChange: (val: string | null) => void;
  placeholder: string;
  variant: 'brand' | 'dark' | 'ghost';
  disabled?: boolean;
  allLabel?: string;
  showAllOption?: boolean;
  neutral?: boolean;
}

export const ChipDropdown: React.FC<ChipDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder,
  variant,
  disabled,
  allLabel = 'All',
  showAllOption = true,
  neutral
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const selectedLabel =
    options.find((o) => o.value === value)?.label || placeholder;

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <ChipWrap ref={ref}>
      <Chip
        type="button"
        $variant={variant}
        $neutral={neutral}
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
      >
        <span>{selectedLabel}</span>
        <ChipCaret $open={open}>
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </ChipCaret>
      </Chip>
      {open && (
        <Dropdown>
          <DropdownSearch
            type="text"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <DropdownList>
            {showAllOption && (
              <DropdownItem
                $selected={value === null}
                onClick={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                {allLabel}
              </DropdownItem>
            )}
            {filtered.map((opt) => (
              <DropdownItem
                key={opt.value}
                $selected={opt.value === value}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                {opt.label}
                {opt.value === value && <Check>✓</Check>}
              </DropdownItem>
            ))}
          </DropdownList>
        </Dropdown>
      )}
    </ChipWrap>
  );
};

export interface MultiChipDropdownProps {
  options: { value: string; label: string }[];
  selected: { value: string; label: string }[];
  onChange: (val: { value: string; label: string }[]) => void;
  placeholder: string;
  variant: 'brand' | 'dark' | 'ghost';
  disabled?: boolean;
  allLabel?: string;
  /** Show the leading option that clears the selection (default true) */
  showAllOption?: boolean;
  neutral?: boolean;
}

export const MultiChipDropdown: React.FC<MultiChipDropdownProps> = ({
  options,
  selected,
  onChange,
  placeholder,
  variant,
  disabled,
  allLabel = 'All',
  showAllOption = true,
  neutral
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const selectedLabel =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? selected[0].label
        : `${selected.length} selected`;

  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const filtered = options.filter((option) =>
    option.label.toLowerCase().includes(search.toLowerCase())
  );

  const toggleOption = (option: { value: string; label: string }) => {
    const isSelected = selected.some((item) => item.value === option.value);
    onChange(
      isSelected
        ? selected.filter((item) => item.value !== option.value)
        : [...selected, option]
    );
  };

  return (
    <ChipWrap ref={ref}>
      <Chip
        type="button"
        $variant={variant}
        $neutral={neutral}
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
      >
        <span>{selectedLabel}</span>
        <ChipCaret $open={open}>
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </ChipCaret>
      </Chip>
      {open && (
        <Dropdown>
          <DropdownSearch
            type="text"
            placeholder="Search…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            autoFocus
          />
          <DropdownList>
            {showAllOption && (
              <DropdownItem
                $selected={selected.length === 0}
                onClick={() => onChange([])}
              >
                {allLabel}
              </DropdownItem>
            )}
            {filtered.map((option) => {
              const isSelected = selected.some(
                (item) => item.value === option.value
              );
              return (
                <DropdownItem
                  key={option.value}
                  $selected={isSelected}
                  onClick={() => toggleOption(option)}
                >
                  {option.label}
                  {isSelected && <Check>✓</Check>}
                </DropdownItem>
              );
            })}
          </DropdownList>
        </Dropdown>
      )}
    </ChipWrap>
  );
};

// ─── Icons ───────────────────────────────────────────────────────────────────

const OnTradeIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 11l2-7h14l2 7" />
    <rect x="3" y="11" width="18" height="8" rx="1" />
  </svg>
);

const OffTradeIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 2l1-2h10l1 2v5l-2 3v11h-8v-11l-2-3z" />
  </svg>
);

// ─── Styled Components ───────────────────────────────────────────────────────

export const Wrapper = styled.div`
  margin-bottom: 20px;
`;

export const ScopeSummary = styled.div`
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink-faint);
  margin: 0 4px 8px;
`;

export const Bar = styled.div`
  display: flex;
  align-items: stretch;
  background: var(--white);
  border-radius: 14px;
  padding: 0 6px;
  box-shadow: 0 1px 3px rgba(43, 61, 84, 0.04);
  border: 1px solid var(--line-soft, #e8e5df);
  flex-wrap: wrap;
  row-gap: 0;
  min-height: 56px;
`;

export const Section = styled.div<{ $stacked?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 14px;
  min-height: 56px;

  ${({ $stacked }) =>
    $stacked &&
    css`
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      gap: 11px;
    `}
`;

export const ActionsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-left: 1px solid var(--line-soft, #e8e5df);
  margin-left: auto;
`;

export const Label = styled.span`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-faint);
  white-space: nowrap;
`;

export const BarDivider = styled.div`
  width: 1px;
  background: var(--line-soft, #e8e5df);
  margin: 12px 0;
  flex-shrink: 0;
`;

export const AppliedChipsWrap = styled.div`
  flex-basis: 100%;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 14px 12px;
`;

export const AppliedChipsDivider = styled.div`
  flex-basis: 100%;
  height: 1px;
  background: var(--line-soft, #e8e5df);
  margin-bottom: 10px;
`;

export const AppliedChipsTitle = styled.span`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-faint);
  white-space: nowrap;
`;

export const AppliedChipsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Toggle = styled.div`
  display: inline-flex;
  background: var(--paper, #f5f3ef);
  border: 1px solid transparent;
  border-radius: 999px;
  padding: 3px;
  gap: 2px;
`;

const ToggleBtn = styled.button<{ $active: boolean; $stacked?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: transparent;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-muted, #7b8794);
  transition:
    background 0.15s,
    color 0.15s;
  white-space: nowrap;

  ${({ $stacked }) =>
    $stacked &&
    css`
      min-width: 100px;
      justify-content: center;
    `}

  ${({ $active }) =>
    $active &&
    css`
      background: var(--terracotta);
      color: var(--white);
    `}

  &:hover:not([disabled]) {
    ${({ $active }) =>
      !$active &&
      css`
        color: var(--ink);
      `}
  }

  svg {
    width: 13px;
    height: 13px;
  }
`;

const ChipWrap = styled.div`
  position: relative;
`;

const Chip = styled.button<{
  $variant: 'brand' | 'dark' | 'ghost';
  $neutral?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  border-radius: 999px;
  padding: 7px 12px 7px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s,
    transform 0.05s;
  white-space: nowrap;

  ${({ $variant, $neutral }) =>
    $variant === 'brand' &&
    (!$neutral
      ? css`
          background: var(--terracotta);
          color: var(--white);
          &:hover {
            background: #b0411a;
          }
        `
      : css`
          background: var(--white);
          color: var(--ink);
          border: 1px solid var(--line);
          svg {
            color: var(--ink-faint);
          }
          &:hover {
            border-color: var(--terracotta);
            color: var(--terracotta);
          }
        `)}

  ${({ $variant, $neutral }) =>
    $variant === 'dark' &&
    (!$neutral
      ? css`
          background: #2b3d54;
          color: var(--white);
          &:hover {
            background: #1f2d40;
          }
        `
      : css`
          background: var(--white);
          color: var(--ink);
          border: 1px solid var(--line);
          svg {
            color: var(--ink-faint);
          }
          &:hover {
            background: var(--paper, #f5f3ef);
            border-color: var(--terracotta);
          }
        `)}

  ${({ $variant }) =>
    $variant === 'ghost' &&
    css`
      background: var(--paper);
      color: var(--ink);
      border: 1px solid var(--line);
      &:hover {
        border-color: var(--terracotta);
        color: var(--terracotta);
        background: var(--white);
      }
    `}

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ChipCaret = styled.span<{ $open: boolean }>`
  display: flex;
  align-items: center;
  opacity: 0.85;
  transition: transform 0.2s;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0)')};
`;

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 200px;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: 12px;
  box-shadow:
    0 8px 32px rgba(43, 61, 84, 0.12),
    0 2px 6px rgba(43, 61, 84, 0.06);
  z-index: 50;
  animation: ${slideDown} 0.12s ease-out;
  overflow: hidden;
`;

const DropdownSearch = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: none;
  border-bottom: 1px solid var(--line-soft, #e8e5df);
  font-size: 13px;
  color: var(--ink);
  outline: none;

  &::placeholder {
    color: var(--ink-faint);
  }

  &:focus {
    background: var(--paper, #f5f3ef);
  }
`;

const DropdownList = styled.div`
  max-height: 240px;
  overflow-y: auto;
  padding: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--line);
    border-radius: 3px;
  }
`;

const DropdownItem = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  color: var(--ink);
  transition: background 0.1s;

  ${({ $selected }) =>
    $selected &&
    css`
      background: var(--paper, #f5f3ef);
      font-weight: 600;
    `}

  &:hover {
    background: var(--paper-warm, #f8f5f0);
  }
`;

const Check = styled.span`
  color: var(--terracotta);
  font-size: 14px;
  font-weight: 600;
`;

export const ResetBtn = styled.button`
  border: none;
  background: none;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink-faint);
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 999px;
  transition:
    background 0.15s,
    color 0.15s;

  &:hover {
    background: var(--paper, #f5f3ef);
    color: var(--ink);
  }
`;

const PeriodSelect = styled.select`
  background: var(--paper, #f5f3ef);
  border: 1px solid var(--line-soft, #e8e5df);
  border-radius: 999px;
  padding: 7px 28px 7px 14px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink);
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%237B8794' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 11px;
  outline: none;

  &:hover {
    border-color: var(--terracotta);
    color: var(--terracotta);
  }
`;

export const FiltersBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--white);
  border: 1px solid var(--terracotta);
  border-radius: 999px;
  padding: 7px 16px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--terracotta);
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(200, 75, 31, 0.08);
  }

  svg {
    width: 13px;
    height: 13px;
  }
`;

export const FiltersBadge = styled.span<{ $zero: boolean }>`
  display: inline-grid;
  place-items: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: ${({ $zero }) => ($zero ? 'var(--line)' : 'var(--terracotta)')};
  color: var(--white);
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
`;

/** Reusable page-specific toolbar — separate white container below the context bar */
export const ContextToolbar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
  padding: 12px 18px;
  background: var(--white);
  border-radius: 14px;
  border: 1px solid var(--line-soft, #e8e5df);
  box-shadow: 0 1px 3px rgba(43, 61, 84, 0.04);
`;

export const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex-basis: 100%;
`;

export const AppliedChipEl = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  background: var(--white);
  border: 1px solid var(--line);
  color: var(--ink);
  font-size: 12px;
  font-weight: 500;
`;

export const ChipCategory = styled.span`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--terracotta);
  background: var(--terracotta-soft);
  padding: 2px 6px;
  border-radius: 4px;
`;

export const AppliedChipX = styled.button`
  border: none;
  background: none;
  color: var(--ink-faint);
  font-size: 14px;
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:hover {
    color: var(--ink);
  }
`;

const AppliedChipWarn = styled.span`
  display: inline-flex;
  width: 14px;
  height: 14px;
  color: #c0770f;
  cursor: help;

  svg {
    width: 100%;
    height: 100%;
  }
`;

export const AppliedChipWarning: React.FC<{ message: string }> = ({
  message
}) => (
  <AppliedChipWarn title={message} aria-label={message} role="img">
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  </AppliedChipWarn>
);

export const FilterIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);
